import React from 'react';
import { HoverPopover } from '@components/ui/HoverPopover';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { Box, Divider, Typography } from '@mui/material';

import { type MetricTileData } from './MetricTile';

/**
 * The "?" affordance in a metric tile header. Hovering (or focusing) it reveals
 * a card explaining what the metric shows and how it's measured — same hover
 * popover the calendar uses. Renders nothing when the metric carries no help.
 */
export const MetricInfoHover: React.FC<{
  name: string;
  help: MetricTileData['help'];
}> = ({ name, help }) => {
  if (!help) return null;

  return (
    <HoverPopover
      placement="top-start"
      role="tooltip"
      content={
        <Box
          sx={{
            width: 300,
            maxWidth: '90vw',
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'grey.200',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.14)',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
            {name}
          </Typography>
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: 'text.secondary' }}>
            {help.body}
          </Typography>
          {help.trackedBy && (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <TravelExploreIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                  }}
                >
                  How it&apos;s tracked
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: 'text.secondary' }}>
                {help.trackedBy}
              </Typography>
            </>
          )}
        </Box>
      }
    >
      <HelpOutlineIcon
        tabIndex={0}
        role="button"
        aria-label={`What is "${name}"?`}
        sx={{
          fontSize: 15,
          color: 'text.disabled',
          cursor: 'help',
          outline: 'none',
          transition: 'color 120ms',
          '&:hover, &:focus-visible': { color: 'text.secondary' },
        }}
      />
    </HoverPopover>
  );
};
